<template>
    <div class="date-picker">
        <calendar
            class="date-picker-calendar"
            :min-date="minDate"
            :max-date="maxDate"
            :disabled-dates="disabledDates"
            :masks="{ weekdays: 'WWW' }"
            :attributes="attributes"
            locale="en"
            is-expanded
            trim-weeks
            :from-date="selectedDateInstance"
            @dayclick="onDayClick"
            @daymouseenter="onMouseEnter"
        />
    </div>
</template>
<script>
import moment from 'moment';
import Calendar from 'v-calendar/lib/components/calendar.umd';

export default {
    components: {
        Calendar,
    },

    model: {
        event: 'change',
        prop: 'value',
    },

    props: {
        minDate: {
            type: Date,
            default: null,
        },
        maxDate: {
            type: Date,
            default: null,
        },
        value: {
            type: Object,
            default: () => ({ start: null, end: null }),
        },
        disabled: {
            default: false,
            type: Boolean,
        },
    },

    data: () => ({
        start: null,
        end: null,
        temporaryStart: null,
        temporaryEnd: null,
    }),

    computed: {
        /**
         * Returns native Date instance of selected date.
         *
         * @returns {Date|null} Native Date instance of selected date.
         */
        selectedDateInstance() {
            if (this.end) {
                return moment(this.end).toDate();
            }

            return null;
        },

        /**
         * Returns list of attributes for days in calendar.
         *
         * @returns {object[]} Attributes for days in calendar.
         */
        attributes() {
            const attributes = [
                this.highlightDate(moment().startOf('day').toDate(), { class: 'today' }),
            ];

            if ((this.start || this.temporaryStart) && (this.end || this.temporaryEnd)) {
                attributes.push(this.highlightRange(
                    this.start || this.temporaryStart,
                    this.end || this.temporaryEnd
                ));
            }

            if (this.start || this.temporaryStart) {
                attributes.push(this.highlightDate(this.start || this.temporaryStart));
            }

            if (this.end || this.temporaryEnd) {
                attributes.push(this.highlightDate(this.end || this.temporaryEnd));
            }

            return attributes;
        },

        /**
         * Returns list of dates to disable.
         *
         * Entire calendar is locked when `disabled` prop is set to true.
         *
         * @returns {object|null} Set of dates to disable.
         */
        disabledDates() {
            return this.disabled ? { start: null, end: null } : null;
        },
    },

    watch: {
        value: {
            immediate: true,

            /**
             * Handle changes of range value.
             *
             * @param  {object|null} value Range value.
             */
            handler(value) {
                const { start = null, end = null } = value || {};

                this.start = start;
                this.end = end;
            },
        },
    },

    methods: {
        /**
         * Highlights given date.
         *
         * @param   {string|Date} date      Date to highlight
         * @param   {object}      highlight Highlight parameters
         * @returns {object}                Highlight configuration.
         */
        highlightDate(date, highlight = { contentClass: 'active' }) {
            return {
                dates: date instanceof Date ? date : moment(date).toDate(),
                highlight,
            };
        },

        /**
         * Highlights given range.
         *
         * @param   {string|Date} start     Start date of range to highlight
         * @param   {string|Date} end       End date of range to highlight
         * @param   {object}      highlight Highlight parameters
         * @returns {object}                Highlight configuration.
         */
        highlightRange(start, end, highlight = { class: 'active' }) {
            return {
                dates: {
                    start: moment(start).toDate(),
                    end: moment(end).toDate(),
                },
                highlight,
            };
        },

        /**
         * Handles click on calendar day.
         *
         * @param  {object} day Day instance.
         */
        onDayClick(day) {
            // Clear range if clicked when range was selected.
            if (null !== this.start && null !== this.end) {
                this.start = null;
                this.end = null;

                this.$emit('change', { start: this.start, end: this.end });
            }

            // Fill temporary start value to not trigger watcher.
            if (null === this.start && null === this.temporaryStart) {
                this.temporaryStart = day.id;
                return;
            }

            // Fill original values (start and end), clear temporary values.
            if (null === this.end) {
                this.start = this.temporaryStart;
                this.end = day.id;

                this.temporaryStart = null;
                this.temporaryEnd = null;

                // Order values.
                if (this.start > this.end) {
                    [this.start, this.end] = [this.end, this.start];
                }

                this.$emit('change', { start: this.start, end: this.end });
            }
        },

        /**
         * Handles entering day with mouse.
         *
         * @param  {object} day Day instance.
         */
        onMouseEnter(day) {
            if (! this.temporaryStart || this.end || day.isDisabled) {
                return;
            }

            this.temporaryEnd = day.id;
        },
    },
};
</script>
